import { Trees, Layers, Wind } from "lucide-react";
import ModelViewer from "@/components/landing/Model3D";

const features = [
  {
    Icon: Trees,
    title: "Weg von der Monokultur",
    desc: "Der europäische Holzbau basiert fast vollständig auf Nadelholz aus Plantagenwirtschaft. MONOWOOD bricht diese Abhängigkeit und integriert standortheimische Laubhölzer in den Bauteilaufbau.",
  },
  {
    Icon: Layers,
    title: "Holzwahl nach Bauphysik",
    desc: "Jede Bauteilschicht erfüllt eine andere Funktion — Tragwerk, Witterung, Dämmung, Akustik, Speichermasse. Wir wählen pro Schicht das Holz, das diese Funktion am besten erfüllt.",
  },
  {
    Icon: Wind,
    title: "Komplettbauteil ab Werk",
    desc: "Inklusive Fassadenschicht werkseitig vorgefertigt. Plug-and-Build statt klassischer Schichtmontage auf der Baustelle.",
  },
];

const Technology = () => {
  return (
    <section
      id="technologie"
      data-testid="technology-section"
      className="relative py-24 md:py-36 bg-[#080808] border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="md:col-span-5 md:sticky md:top-32">
            <span data-testid="tech-eyebrow" className="label-eyebrow">
              Das System
            </span>
            <h2 className="font-heading mt-6 text-4xl sm:text-5xl font-light leading-[1.05] tracking-tighter">
              Holz nach Funktion.
              <br />
              <span className="text-[#c88a53] italic font-extralight">
                Für jede Bauteilschicht.
              </span>
            </h2>
            <p className="mt-8 text-base md:text-lg text-white/65 leading-relaxed font-light">
              Jede Schicht eines Bauteils hat eine eigene Funktion — und wird
              bei MONOWOOD aus der dafür optimal geeigneten Holzsorte gefertigt.
              Statt einer Holzart für alles: das richtige Holz für jede Aufgabe.
            </p>
          </div>

          <div className="md:col-span-7">
            <div className="space-y-px bg-white/10">
              {features.map((f, i) => (
                <div
                  key={i}
                  data-testid={`tech-feature-${i}`}
                  className="bg-[#0a0a0a] p-8 md:p-10 group hover:bg-[#0f0f0f] transition-colors"
                >
                  <div className="flex items-start gap-6">
                    <div className="shrink-0 w-12 h-12 flex items-center justify-center border border-white/10 group-hover:border-[#c88a53]/60 transition-colors">
                      <f.Icon size={20} className="text-[#c88a53]" strokeWidth={1.4} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-baseline justify-between gap-4">
                        <h3 className="font-heading text-xl md:text-2xl font-medium text-white tracking-tight">
                          {f.title}
                        </h3>
                        <span className="text-[0.65rem] tracking-[0.3em] uppercase text-white/30 font-mono-num">
                          0{i + 1}
                        </span>
                      </div>
                      <p className="mt-3 text-sm md:text-base text-white/55 leading-relaxed">
                        {f.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Process strip — Design → Fertigung → Montage */}
            <div className="mt-10 border border-white/10 p-8 md:p-10 bg-[#0a0a0a]">
              <span className="label-eyebrow">Der Prozess</span>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-6">
                {[
                  { n: "01", t: "Design", d: "Parametrische Planung im MONOWOOD-System." },
                  { n: "02", t: "Fertigung", d: "Werkseitige Vorfertigung des Komplettbauteils." },
                  { n: "03", t: "Montage", d: "Aufbau in Tagen, nicht Monaten." },
                ].map((s, i) => (
                  <div key={i} data-testid={`process-step-${i}`}>
                    <div className="font-heading text-[#c88a53] font-mono-num text-sm">
                      {s.n}
                    </div>
                    <div className="mt-2 font-heading text-white text-lg">
                      {s.t}
                    </div>
                    <div className="mt-1 text-sm text-white/55 leading-relaxed">
                      {s.d}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Interaktives 3D-Modell */}
        <div className="mt-16">
          <ModelViewer />
        </div>
      </div>
    </section>
  );
};

export default Technology;
